import {ClassValue, clsx} from 'clsx'
import dayjs, {Dayjs} from 'dayjs'
import isArray from 'lodash/isArray'
import React from 'react'

import {PROJECTS, settings} from '@/src/config/settings'

import {isIOS, isMobile} from './platform'

import {Link} from '@/src/shared/ui/link'

export function uuid() {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
    // @ts-expect-error ...
    (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
  )
}

export function onlyDigits(word: string) {
  return word.replace(/([^\d]+)/gi, '')
}

export function verboseCurrency(value: number, withCurrencySymbol = true, digits = 0) {
  const formatter = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: digits,
  })

  let formattedValue = formatter.format(value)

  if (withCurrencySymbol) return formattedValue

  formattedValue = formattedValue.replace('₽', '').trim()

  while (/\u00A0/g.test(formattedValue)) {
    formattedValue = formattedValue.replace(/\u00A0/g, '')
  }

  return formattedValue
}

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs)
}

export const fillNavLink = (
  navItem: LayoutMenuItem,
  index: React.Key,
  popupClassName?: string,
  forcePopUpClassname?: boolean,
  withoutLink?: boolean,
) => {
  const item: any & {
    popupClassName?: string
  } = {
    ...navItem,
    type: undefined,
    key: index,
  }

  if (withoutLink) {
    item.label = item.title
  } else {
    item.label = React.createElement(Link, {
      href: item.url as string,
      child: item.title as React.ReactNode,
    })
  }

  item.classNames = {
    subMenu: {
      list: popupClassName,
    },
  }

  if (forcePopUpClassname && popupClassName) {
    item.popupClassName = popupClassName
  }

  if ('children' in item && isArray(item.children) && item.children.length > 0) {
    item.children = item.children.map((child: LayoutMenuItem, childIndex: number) =>
      fillNavLink(child, `${index.toString()}-${childIndex}`, popupClassName, true),
    )
  } else if ('children' in item) {
    delete item['children']
  }

  return item as NavItem
}
export const headerHeight = () => {
  return nodeHeight('[data-header]')
}
export const mobileMenuHeight = () => {
  return nodeHeight('[data-mobile-menu]')
}
export const nodeHeight = (selector: string) => {
  if (typeof window === 'undefined') return 0

  return new Array(document.querySelectorAll(selector).entries()).reduce((height, iter) => {
    const iterValue = iter.next().value
    if (iterValue) {
      // @ts-ignore
      const [, el] = iterValue
      return el.getBoundingClientRect().height + height
    }
    return height
  }, 0)
}
export const scrollTo = (el: HTMLDivElement) => {
  if (typeof window === 'undefined') return

  window.scroll({
    top: el.getBoundingClientRect().top + window.scrollY - headerHeight() - 20,
    // behavior: 'smooth',
  })
}
export const handleDateValue = (value: Dayjs | string) => {
  if (value) {
    return dayjs(value).format('YYYY-MM-DD')
  }
  return value
}
type DropFirst<T extends any[]> = T extends [any, ...infer R] ? R : never
export const storeRef = <Fn extends (element: any, ...args: any[]) => void>(
  storeFn: Fn,
  ...passToFnArgs: DropFirst<Parameters<Fn>>
) => {
  return (element: any) => {
    if (element && typeof window != 'undefined') {
      storeFn.call(undefined, element!, ...passToFnArgs)
    }
  }
}
export const isWeb = () => settings.PROJECT === PROJECTS.Web

export const getRunlabClient = (): 'web' | 'ios' | 'android' => {
  if (isWeb()) {
    return 'web'
  }

  if (isMobile() && isIOS()) {
    return 'ios'
  }

  return 'android'
}

export {isMobile, isIOS} from './platform'

export class TaskQueue {
  queue: any[] = []
  isProcessing: boolean = false

  async enqueue(task: (...args: any[]) => any) {
    return new Promise((resolve, reject) => {
      this.queue.push({task, resolve, reject})
      this.processQueue()
    })
  }

  async processQueue() {
    if (this.isProcessing) return

    this.isProcessing = true

    while (this.queue.length > 0) {
      const {task, resolve, reject} = this.queue.shift()

      try {
        const result = await task()
        resolve(result)
      } catch (error) {
        reject(error)
      }
    }

    this.isProcessing = false
  }
}

export const replaceMobileImageUrls = () => {
  if (isMobile()) {
    document.querySelectorAll('img').forEach(img => {
      if (img.src.startsWith(window.location.origin) && img.src.includes('/upload/')) {
        img.src = img.src.replace(window.location.origin, settings.NEXT_PUBLIC_FRONTEND_URL)
      }
    })
  }
}

export function getClientCookie(name: string) {
  let cookieValue = null
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i]?.trim()
      if (cookie) {
        // Does this cookie string begin with the name we want?
        if (cookie.substring(0, name.length + 1) === name + '=') {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
          break
        }
      }
    }
  }
  return cookieValue
}

export function sanitizeHtml(html: string | undefined): string {
  return html ?? ''
}

export function getFormErrorKeys<T extends Record<string, any>>(
  errors: Partial<Record<keyof T, {message?: string}>>,
): (keyof T)[] {
  return Object.keys(errors) as (keyof T)[]
}

export const isAppleClient = () => {
  if (typeof window === 'undefined') {
    return false
  }
  const ua = navigator.userAgent.toLowerCase()

  return /iphone|ipad|ipod|macintosh/.test(ua)
}

export const formatDateForAPIFormat = (dateString: string): string => {
  return dateString
    .replace(/(\d+)\.(\d+)\.(\d+)/, '$3-$2-$1')
    .split('-')
    .map(datePart => {
      if (datePart.length === 1) {
        return `0${datePart}`
      }
      return datePart
    })
    .join('-')
}

export const scrollToHeader = () => {
  const headerEl = document.querySelector('body')

  if (headerEl) {
    headerEl.scrollIntoView()
  }
}
